/**
 * ConnectorWizard step bodies (extracted from the orchestrator).
 *
 * Each step is a stateless render component that consumes the wizard's
 * state + handlers via a single `ctx` prop bag — matching the pattern
 * used in `components/data/tabs/*` for Data.jsx Phase 3.
 *
 * The orchestrator (`ConnectorWizard.jsx`) still owns all useState calls
 * and computed selectors; this file just contains the JSX. Future work
 * can keep migrating the remaining 4 steps (configure, test, accounts,
 * footer-actions) here without touching state ownership.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
    ChevronRight, ChevronLeft, ChevronDown, Database, TableProperties, Loader2, Plus, Link2,
    Trash2, Clock, Sparkles, Layers, PlugZap, Zap, CheckCircle2, XCircle, Wand2, AlertCircle,
    KeyRound, SlidersHorizontal, Settings2,
} from "lucide-react";
import SchemaMatchPreview from "@/components/data/SchemaMatchPreview";


/**
 * Configure step — section primitives (universal, driver-agnostic).
 *
 * This is the STANDARD Configure-page pattern for EVERY connector: native
 * SDK drivers, Python drivers, YAML connectors and YAML→Python compiled
 * drivers all reach the wizard through the same `driver.fields` payload, so
 * they all inherit this layout automatically — no per-driver code:
 *   1. Connection details — connection name + non-secret fields
 *   2. Credentials        — OAuth credential set + secrets / auth fields
 *   3. Advanced settings  — collapsed: OAuth overrides + optional tuning
 *   …followed by a single-line "Use sample data" switch at the very bottom.
 */
function ConfigureSection({ title, description, right, children, testId }) {
    return (
        <section className="rounded-sm border border-border bg-card" data-testid={testId}>
            <div className="px-4 pt-3.5 pb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
                    {description && (
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{description}</p>
                    )}
                </div>
                {right && <div className="shrink-0 text-[11px] pt-0.5" data-testid={`${testId}-status`}>{right}</div>}
            </div>
            <div className="px-4 pb-4">{children}</div>
        </section>
    );
}

function AdvancedSection({ badge, description, open, onToggle, children, testId }) {
    return (
        <section className="rounded-sm border border-border bg-card" data-testid={testId}>
            <button
                type="button"
                onClick={() => onToggle(!open)}
                aria-expanded={open}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-secondary/40 transition-colors"
                data-testid={`${testId}-toggle`}
            >
                <span className="flex items-center gap-2.5 min-w-0">
                    <span className="text-sm font-semibold tracking-tight shrink-0">Advanced settings</span>
                    {badge && (
                        <span className="shrink-0 text-[10px] px-1.5 py-px rounded-sm border border-border text-muted-foreground">
                            {badge}
                        </span>
                    )}
                    {description && (
                        <span className="hidden md:inline text-[11px] text-muted-foreground truncate">{description}</span>
                    )}
                </span>
                <ChevronRight className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
            </button>
            {open && <div className="px-4 pb-4 pt-3 border-t border-border/60">{children}</div>}
        </section>
    );
}

/** Responsive field grid: inputs in 1/2/3 columns (textareas span the
 *  row), boolean options as a compact checkbox row underneath. */
function FieldGrid({ fields, render, lead = null }) {
    const inputs = fields.filter((f) => f.type !== "boolean");
    const toggles = fields.filter((f) => f.type === "boolean");
    const hasGrid = !!lead || inputs.length > 0;
    return (
        <>
            {hasGrid && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-4">
                    {lead}
                    {inputs.map((f) => (
                        <div key={f.key} className={f.type === "textarea" ? "min-w-0 md:col-span-2 xl:col-span-3" : "min-w-0"}>
                            {render(f)}
                        </div>
                    ))}
                </div>
            )}
            {toggles.length > 0 && (
                <div className={`flex flex-wrap gap-x-8 gap-y-2 ${hasGrid ? "mt-4" : ""}`}>
                    {toggles.map((f) => <div key={f.key} className="min-w-0">{render(f)}</div>)}
                </div>
            )}
        </>
    );
}


// ─────────────────────────────────────────────────────────────────────────
// Field classifier — universal, driver-agnostic. Any future connector
// (native, Python, YAML) inherits the correct section automatically from
// its declared `key` / `type` / `group` / `required` metadata.
// ─────────────────────────────────────────────────────────────────────────
const SECONDARY_GROUPS = new Set(["options", "schedule", "advanced", "performance", "sync", "tuning"]);
const CREDENTIAL_GROUPS = new Set(["credentials", "credential", "authentication", "auth"]);
const CREDENTIAL_KEYS = new Set(["auth_mode", "auth_method", "auth_type", "username", "user", "login"]);
// Key-name pattern for secrets/identities declared as plain text by some
// drivers (e.g. S3 `access_key_id`, a YAML `api_key`) — keeps them in the
// Credentials section for current and future connectors alike.
const CREDENTIAL_KEY_RE = /(access_key|api_key|secret|token|password|passphrase|private_key)/i;

function classifyField(field, oauthMergeFields) {
    // Commented out to allow manual entry of refresh tokens for connectors (e.g. Google Ads, GA4, Sheets):
    // if (["oauth_refresh_token", "refresh_token", "oauth_access_token"].includes(field.key)) {
    //     return "hidden";
    // }
    // Sample-data mode is a mode switch, not a setup field → rendered as
    // the single-line option at the bottom of the page.
    if (field.key === "use_sample_data") return "sample";
    // Fields that can be sourced from Settings → OAuth credentials (e.g.
    // client_id, client_secret, app_id, app_secret) → per-connector
    // overrides, shown inside Advanced settings.
    if (Array.isArray(oauthMergeFields) && oauthMergeFields.includes(field.key)) return "advanced";
    const group = String(field.group || "").trim().toLowerCase();
    // Optional tuning (Options / Schedule / Advanced groups) stays out of
    // the way. Required fields are never hidden in the collapsed section.
    if (!field.required && SECONDARY_GROUPS.has(group)) return "advanced";
    // Secrets (password-typed), auth-group fields and login identity.
    if (field.type === "password" || CREDENTIAL_GROUPS.has(group) || CREDENTIAL_KEYS.has(field.key)
        || CREDENTIAL_KEY_RE.test(field.key || "")) {
        return "credentials";
    }
    // Everything else (customer_id, host, port, property_id, …).
    return "connection";
}


function ConfidenceBadge({ value }) {
    if (value == null) return <span className="text-muted-foreground">—</span>;
    const v = Math.round(value * 100);
    const cls = v >= 80 ? "border-[hsl(var(--success))] text-[hsl(var(--success))]"
              : v >= 50 ? "border-amber-500 text-amber-500"
              :          "border-muted-foreground text-muted-foreground";
    return <Badge variant="outline" className={`font-mono text-[9px] ${cls}`}>{v}%</Badge>;
}


function TableColSelect({ tables, table, col, colsFor, onTable, onCol, testId }) {
    const cols = table ? (colsFor(table) || []) : [];
    return (
        <div className="flex items-center gap-1">
            <select value={table || ""} onChange={(e) => onTable(e.target.value)}
                className="h-7 px-1 bg-transparent border border-border rounded-sm text-xs font-mono max-w-[140px]"
                data-testid={`${testId}-table`}>
                <option value="">—</option>
                {tables.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <span className="text-muted-foreground">.</span>
            {cols.length > 0 ? (
                <select value={col || ""} onChange={(e) => onCol(e.target.value)}
                    className="h-7 px-1 bg-transparent border border-border rounded-sm text-xs font-mono max-w-[120px]"
                    disabled={!table}
                    data-testid={`${testId}-col`}>
                    <option value="">—</option>
                    {cols.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
            ) : (
                <input
                    type="text"
                    value={col || ""}
                    onChange={(e) => onCol(e.target.value)}
                    placeholder="column"
                    disabled={!table}
                    className="h-7 px-1.5 bg-transparent border border-border rounded-sm text-xs font-mono max-w-[120px]"
                    data-testid={`${testId}-col`}
                />
            )}
        </div>
    );
}


/* ============================================================
   Step: DATE RANGE — pick relative lookback or absolute window
   ============================================================ */
export function DateRangeStep({ ctx }) {
    const { dateRange, setDateRange, previewRows } = ctx;
    const kind = dateRange?.kind || "relative";
    const lookbackPresets = [7, 30, 90, 180, 365];
    const lookback = dateRange?.lookback_days ?? 180;
    const startDate = dateRange?.start_date || "";
    const endDate = dateRange?.end_date || "";
    const [preview, setPreview] = React.useState({ loading: false, result: null, error: null });

    const setKind = (k) =>
        setDateRange((prev) => ({ ...(prev || {}), kind: k }));
    const setLookback = (n) =>
        setDateRange((prev) => ({ ...(prev || {}), kind: "relative", lookback_days: Number(n) || 180 }));
    const setStart = (s) =>
        setDateRange((prev) => ({ ...(prev || {}), kind: "absolute", start_date: s }));
    const setEnd = (s) =>
        setDateRange((prev) => ({ ...(prev || {}), kind: "absolute", end_date: s }));

    // Reset preview when date_range changes — stale numbers are worse than none.
    React.useEffect(() => {
        setPreview({ loading: false, result: null, error: null });
    }, [kind, lookback, startDate, endDate]);

    const runPreview = async () => {
        if (!previewRows) return;
        setPreview({ loading: true, result: null, error: null });
        try {
            const r = await previewRows({ kind, lookback_days: lookback, start_date: startDate, end_date: endDate });
            if (!r || r.supported === false) {
                setPreview({ loading: false, result: null, error: "This connector does not support row-count previews." });
                return;
            }
            if (r.error) {
                setPreview({ loading: false, result: null, error: r.error });
                return;
            }
            setPreview({ loading: false, result: r, error: null });
        } catch (e) {
            setPreview({ loading: false, result: null, error: e?.message || "Preview failed" });
        }
    };

    return (
        <div data-testid="wiz-step-date-range" className="space-y-4">
            <div>
                <div className="eyebrow">Date range</div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                    Choose how far back the connector should pull data for every sync. Affects every selected account.
                </p>
            </div>

            {/* Kind toggle */}
            <div className="flex items-center gap-2" role="tablist">
                <button
                    type="button"
                    onClick={() => setKind("relative")}
                    className={`px-3 h-8 text-[11px] font-mono uppercase tracking-wider border rounded-sm transition-colors ${
                        kind === "relative" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground/50"
                    }`}
                    data-testid="wiz-date-range-relative"
                >
                    Relative lookback
                </button>
                <button
                    type="button"
                    onClick={() => setKind("absolute")}
                    className={`px-3 h-8 text-[11px] font-mono uppercase tracking-wider border rounded-sm transition-colors ${
                        kind === "absolute" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground/50"
                    }`}
                    data-testid="wiz-date-range-absolute"
                >
                    Absolute window
                </button>
            </div>

            {kind === "relative" && (
                <div className="border border-border rounded-sm bg-card p-4 space-y-3" data-testid="wiz-date-range-relative-card">
                    <div className="eyebrow">Lookback</div>
                    <div className="flex flex-wrap gap-2">
                        {lookbackPresets.map((d) => (
                            <button
                                key={d}
                                type="button"
                                onClick={() => setLookback(d)}
                                className={`px-3 h-8 text-[11px] font-mono uppercase tracking-wider border rounded-sm transition-colors ${
                                    Number(lookback) === d ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground/50"
                                }`}
                                data-testid={`wiz-date-range-preset-${d}`}
                            >
                                Last {d}d
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Custom:</span>
                        <input
                            type="number"
                            min={1}
                            max={3650}
                            value={lookback}
                            // iter-120 (Feb 2026) — allow the field to be
                            // temporarily empty so the user can backspace
                            // the entire value and retype. We only fall
                            // back to the default when the field LOSES
                            // FOCUS — otherwise typing was impossible
                            // because we kept snapping back to 180.
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === "") {
                                    setDateRange((prev) => ({ ...(prev || {}), kind: "relative", lookback_days: "" }));
                                } else {
                                    setLookback(raw);
                                }
                            }}
                            onBlur={(e) => {
                                const n = Number(e.target.value);
                                if (!Number.isFinite(n) || n < 1) {
                                    setLookback(180);
                                }
                            }}
                            className="w-24 h-8 px-2 bg-transparent border border-border rounded-sm font-mono text-xs"
                            data-testid="wiz-date-range-lookback-days"
                        />
                        <span className="text-muted-foreground">days back from today (UTC)</span>
                    </div>
                </div>
            )}

            {kind === "absolute" && (
                <div className="border border-border rounded-sm bg-card p-4 space-y-3" data-testid="wiz-date-range-absolute-card">
                    <div className="eyebrow">Custom window</div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">From</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStart(e.target.value)}
                                className="w-full h-9 px-2 bg-transparent border border-border rounded-sm font-mono text-xs"
                                data-testid="wiz-date-range-start"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">To</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEnd(e.target.value)}
                                className="w-full h-9 px-2 bg-transparent border border-border rounded-sm font-mono text-xs"
                                data-testid="wiz-date-range-end"
                            />
                        </div>
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                        Both dates inclusive (ISO yyyy-mm-dd). Connector must support absolute ranges — falls back to a 180-day lookback otherwise.
                    </div>
                </div>
            )}

            {/* Preview rows widget — only when the orchestrator passed the
                `previewRows` callback (drivers that support preview_rowcount). */}
            {previewRows && (
                <div className="border border-border rounded-sm bg-card p-4 space-y-2" data-testid="wiz-date-range-preview-card">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="eyebrow">Estimated rows for this window</div>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                                A quick row-count check before ingest. Sample mode is deterministic; live mode runs one summary query against the provider.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={runPreview}
                            disabled={preview.loading}
                            className="px-3 h-8 text-[11px] font-mono uppercase tracking-wider border border-primary text-primary rounded-sm hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="wiz-date-range-preview-btn"
                        >
                            {preview.loading ? "Previewing…" : (preview.result ? "Refresh" : "Preview rows")}
                        </button>
                    </div>
                    {preview.error && (
                        <div className="border border-destructive/40 bg-destructive/5 rounded-sm px-3 py-2 text-[11px] font-mono" data-testid="wiz-date-range-preview-error">
                            ⚠ {preview.error}
                        </div>
                    )}
                    {preview.result && (
                        <div className="flex items-center gap-3 pt-1" data-testid="wiz-date-range-preview-result">
                            <div className="text-2xl font-semibold tracking-tight" data-testid="wiz-date-range-preview-count">
                                {Number(preview.result.estimated_rows || 0).toLocaleString()}
                            </div>
                            <div className="text-[10px] text-muted-foreground leading-tight">
                                rows · <span className="font-mono uppercase">{preview.result.mode}</span>
                                {preview.result.details?.time_range && (
                                    <span className="block">{preview.result.details.time_range.since} → {preview.result.details.time_range.until}</span>
                                )}
                                {preview.result.details?.sample_n != null && (
                                    <span className="block">seed n = {preview.result.details.sample_n}</span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


/* ============================================================
   Step: TABLES — schema/table picker with incremental cursor
   ============================================================ */
export function TablesStep({ ctx }) {
    const {
        schemas, expandedSchemas, tablesBySchema, selected, busy, driver,
        toggleSchema, toggleTable, setCursor, isTableSelected, isSuperAdmin,
        selectAllInSchema, deselectAllInSchema, dataModelSlug, modelTargets,
    } = ctx;
    // The connection-level target applies to every table (the backend uses it
    // ahead of the driver's per-table defaults), so badges show that target.
    const routesToModel = !!(driver?.target_model_slug || driver?.table_model_map);

    // Group "DATABASE.SCHEMA" identifiers into a Database → Schema hierarchy.
    // Bare names (sample mode: sales/hr/finance) have no dot → rendered flat.
    const [expandedDbs, setExpandedDbs] = React.useState({});
    const toggleDb = (db) => setExpandedDbs((p) => ({ ...p, [db]: !p[db] }));
    const dbOf = (s) => (s && s.includes(".") ? s.slice(0, s.indexOf(".")) : "");
    const schemaLabel = (s) => (s && s.includes(".") ? s.slice(s.indexOf(".") + 1) : s);
    const isDbSentinel = (s) => s && s.includes(".") && s.slice(s.indexOf(".") + 1) === "";

    const groups = React.useMemo(() => {
        const m = new Map();
        for (const s of schemas) {
            const db = dbOf(s);
            if (!m.has(db)) m.set(db, []);
            // Keep only real schema rows; the "DB." sentinel only creates the node.
            if (!isDbSentinel(s)) m.get(db).push(s);
        }
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schemas]);
    const dbKeys = [...groups.keys()];
    const hasDatabases = dbKeys.some((k) => k !== "");

    const renderSchemaRow = (s) => {
        const tablesLoaded = Array.isArray(tablesBySchema[s]);
        const selectedInSchema = selected.filter((t) => t.schema === s).length;
        return (
            <div key={s}>
                <div className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/30">
                    <button onClick={() => toggleSchema(s)} className="flex items-center gap-2 flex-1 min-w-0 text-left"
                            data-testid={`wiz-schema-${s}`}>
                        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expandedSchemas[s] ? "rotate-90" : ""}`} />
                        <Layers className="h-3.5 w-3.5 text-primary" />
                        <span className="font-mono text-sm truncate">{schemaLabel(s)}</span>
                    </button>
                    {tablesLoaded && (
                        <Badge variant="outline" className="font-mono text-[10px] shrink-0">
                            {tablesBySchema[s].length} tables
                        </Badge>
                    )}
                    {tablesLoaded && tablesBySchema[s].length > 0 && (
                        selectedInSchema > 0 ? (
                            <button type="button" onClick={() => deselectAllInSchema(s)}
                                className="shrink-0 text-[10px] font-mono uppercase tracking-wider text-primary hover:underline"
                                data-testid={`wiz-schema-deselect-all-${s}`}>
                                Deselect all
                            </button>
                        ) : (
                            <button type="button" onClick={() => selectAllInSchema(s)}
                                className="shrink-0 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-primary hover:underline"
                                data-testid={`wiz-schema-select-all-${s}`}>
                                Select all
                            </button>
                        )
                    )}
                </div>
                {expandedSchemas[s] && (
                    <div className="bg-secondary/20">
                        {!tablesBySchema[s] && (
                            <div className="px-10 py-3 text-xs text-muted-foreground">
                                <Loader2 className="h-3 w-3 animate-spin inline mr-1.5" />Loading tables…
                            </div>
                        )}
                        {Array.isArray(tablesBySchema[s]) && tablesBySchema[s].length === 0 && (
                            <div className="px-10 py-3 text-xs text-muted-foreground">No tables in this schema.</div>
                        )}
                        {(tablesBySchema[s] || []).map((t) => {
                            const sel = isTableSelected(s, t.name);
                            const selRec = selected.find((x) => x.schema === s && x.name === t.name);
                            const modelSlug = routesToModel ? (dataModelSlug || null) : null;
                            return (
                                <div key={t.name} className="px-10 py-2 border-b border-border last:border-0"
                                    data-testid={t.sa_only ? `wiz-table-row-sa-only-${s}-${t.name}` : `wiz-table-row-${s}-${t.name}`}>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Checkbox checked={sel} onCheckedChange={() => toggleTable(s, t.name, t.columns, t.row_count_estimate)}
                                            data-testid={`wiz-table-${s}-${t.name}`} />
                                        <TableProperties className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="font-mono text-sm">{t.name}</span>
                                        {t.sa_only && (
                                            <Badge variant="outline" className="font-mono text-[9px] gap-1 border-primary/60 text-primary bg-primary/5"
                                                title="SA-only breakdown supplement. Non-SA users always ingest this automatically.">
                                                <KeyRound className="h-2.5 w-2.5" /> SA-only · breakdown
                                            </Badge>
                                        )}
                                        <Badge variant="outline" className="font-mono text-[9px]">{t.columns?.length || 0} cols</Badge>
                                        {t.row_count_estimate != null && (
                                            <Badge variant="outline" className="font-mono text-[9px] text-muted-foreground">
                                                ~{t.row_count_estimate.toLocaleString()} rows
                                            </Badge>
                                        )}
                                        {isSuperAdmin && routesToModel && !modelSlug && modelTargets && (
                                            <Badge variant="outline" className="font-mono text-[9px] gap-1 border-amber-500 text-amber-700 dark:text-amber-400"
                                                title="Choose the target data model on the Confirm mapping step."
                                                data-testid={`wiz-table-model-${s}-${t.name}`}>
                                                <Layers className="h-2.5 w-2.5" /> → choose model
                                            </Badge>
                                        )}
                                        {isSuperAdmin && modelSlug && (
                                            <Badge variant="outline" className={`font-mono text-[9px] gap-1 ${sel ? "border-primary text-primary bg-primary/5" : "text-muted-foreground"}`}
                                                title={`Rows from this table go to the "${modelSlug}" data model chosen for this connection.`}
                                                data-testid={`wiz-table-model-${s}-${t.name}`}>
                                                <Layers className="h-2.5 w-2.5" /> → model.{modelSlug}
                                            </Badge>
                                        )}
                                    </div>
                                    {t.description && (
                                        <div className="ml-7 mt-1 text-[10px] text-muted-foreground italic">{t.description}</div>
                                    )}
                                    {sel && (
                                        <div className="mt-1.5 ml-7 flex items-center gap-2 text-[11px]">
                                            <span className="text-muted-foreground font-mono">Incremental cursor:</span>
                                            <select value={selRec?.incremental_col || ""} onChange={(e) => setCursor(s, t.name, e.target.value)}
                                                className="h-7 px-2 bg-transparent border border-border rounded-sm text-xs font-mono"
                                                data-testid={`wiz-cursor-${s}-${t.name}`}>
                                                <option value="">(full reload every time)</option>
                                                {(t.columns || []).map((c) => (
                                                    <option key={c.name} value={c.name}>{c.name} · {c.type}{c.is_pk ? " · PK" : ""}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div data-testid="wiz-step-tables">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="eyebrow">
                        {hasDatabases
                            ? `Available databases (${dbKeys.length})`
                            : `Available schemas (${schemas.length})`}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        {hasDatabases
                            ? "Expand a database to see its schemas, then a schema to see its tables. Tables across schemas and databases can be selected together — joins are auto-detected on the next step."
                            : "Click any schema to expand. Tables across multiple schemas can be selected together — joins are auto-detected on the next step."}
                    </div>
                </div>
                <div className="text-[11px] font-mono">
                    {selected.length} selected
                </div>
            </div>
            {isSuperAdmin && (tablesBySchema && Object.values(tablesBySchema).some((arr) => (arr || []).some((t) => t.sa_only))) && (
                <div className="mb-3 px-3 py-2 text-[11px] bg-primary/5 border border-primary/30 rounded-sm flex items-start gap-2"
                    data-testid="wiz-sa-only-breakdown-banner">
                    <KeyRound className="h-3.5 w-3.5 mt-0.5 text-primary" />
                    <span className="text-foreground">
                        <b>SA-only breakdown supplements visible.</b> These tables fire ONE Insights call per breakdown axis (device / country / region / combo) and write into <code className="font-mono text-[10px]">model.marketing</code> with a distinct <code className="font-mono text-[10px]">grain</code> value. Non-SA users never see these in the wizard but always ingest all of them. Uncheck to skip per-connector.
                    </span>
                </div>
            )}
            {busy && schemas.length === 0 && (
                <div className="text-center py-10 text-xs text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading schemas…
                </div>
            )}
            <div className="border border-border rounded-sm divide-y divide-border bg-card">
                {hasDatabases ? (
                    dbKeys.map((db) => {
                        const schemaList = groups.get(db);
                        const selInDb = selected.filter((t) => dbOf(t.schema) === db).length;
                        return (
                            <div key={db || "_root"}>
                                <button
                                    type="button"
                                    onClick={() => toggleDb(db)}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-muted/40 text-left"
                                    data-testid={`wiz-db-${db}`}>
                                    <ChevronRight className={`h-4 w-4 transition-transform ${expandedDbs[db] ? "rotate-90" : ""}`} />
                                    <Database className="h-4 w-4 text-primary" />
                                    <span className="font-mono text-sm font-semibold truncate flex-1 min-w-0">{db}</span>
                                    {selInDb > 0 && (
                                        <Badge variant="outline" className="font-mono text-[10px] border-primary/60 text-primary bg-primary/5 shrink-0">
                                            {selInDb} selected
                                        </Badge>
                                    )}
                                    <Badge variant="outline" className="font-mono text-[10px] shrink-0">
                                        {schemaList.length} schema{schemaList.length === 1 ? "" : "s"}
                                    </Badge>
                                </button>
                                {expandedDbs[db] && (
                                    <div className="border-t border-border bg-secondary/10 divide-y divide-border">
                                        {schemaList.length > 0
                                            ? schemaList.map(renderSchemaRow)
                                            : <div className="px-10 py-3 text-xs text-muted-foreground">No browsable schemas in this database.</div>}
                                    </div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    schemas.map(renderSchemaRow)
                )}
                {!busy && schemas.length === 0 && (
                    <div className="px-4 py-6 text-center text-xs text-muted-foreground">No schemas available. Go back and verify the connection.</div>
                )}
            </div>
        </div>
    );
}


/* ============================================================
   Step: RELATIONSHIPS — auto-detected joins + schedule inline
   ============================================================ */
export function RelationshipsStep({ ctx }) {
    const {
        driver,
        relationships, selected, selectedQualifiedTables,
        addRel, updateRel, deleteRel, allColsFor,
        factTable, setFactTable,
        enableSchedule, scheduleHours, setEnableSchedule, setScheduleHours,
        dataModels, dataModelSlug, setDataModelSlug, setDataModels, modelTargets, legacyTarget,
    } = ctx;

    // iter-121.163 Phase 2 — functional Data Model selector.
    // Options are computed from `dataModels` (list fetched by the
    // orchestrator, filtered to the same category as the driver's
    // target_model_slug). System models get a ★ prefix and land at
    // the top; user-created clones follow, sorted by name. Universal —
    // works for every connector that exposes `target_model_slug`
    // (marketing, ga4, campaign, future categories).
    //
    // iter-121.165 — For drivers that ALSO declare a `mapping` step
    // (SQL Server / MySQL / Postgres / any DB driver), the Data Model
    // card is moved to that later step so the SA can review relationships
    // separately from picking a model. This block is therefore hidden
    // when the driver's wizard_steps includes "mapping".
    const targetSlug = driver?.target_model_slug || null;
    const hasMappingStep = (driver?.wizard_steps || []).includes("mapping");
    const showDataModelHere = !!targetSlug && !hasMappingStep;
    const [creating, setCreating] = React.useState(false);
    const [newName, setNewName] = React.useState("");
    const [busyCreate, setBusyCreate] = React.useState(false);
    // Sort: default (system) model of this category first, then other
    // system models, then user-created clones — all alphabetical.
    // Order comes from the backend binding rules (enforced bindings first).
    const sortedModels = dataModels || [];
    const enforcedSlugs = modelTargets?.enforced || [];
    const selectable = (m) => m.slug === dataModelSlug || m.slug === legacyTarget || (m.valid !== false && (!enforcedSlugs.length || enforcedSlugs.includes(m.slug)));
    const selectedModel = sortedModels.find((m) => m.slug === dataModelSlug);
    const isCloned = selectedModel && !selectedModel.is_system_model;
    // Handle dropdown change — special value "__create_new__" enters the
    // inline "give it a name" mode instead of committing an invalid slug.
    const onModelChange = (v) => {
        if (v === "__create_new__") { setCreating(true); return; }
        setDataModelSlug(v);
    };
    const submitCreate = async () => {
        const name = (newName || "").trim();
        if (!name) return;
        if (!targetSlug) return;
        setBusyCreate(true);
        try {
            const api = (await import("@/lib/api")).default;
            const r = await api.post("/data-models/clone", { from_slug: targetSlug, name });
            const created = r.data;
            if (created?.slug) {
                setDataModels((prev) => ([...(prev || []), created]));
                setDataModelSlug(created.slug);
                setCreating(false);
                setNewName("");
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Failed to create model";
            const { toast } = await import("sonner");
            toast.error(msg);
        } finally {
            setBusyCreate(false);
        }
    };
    const cancelCreate = () => { setCreating(false); setNewName(""); };

    return (
        <div data-testid="wiz-step-rels">
            {/* ── Data Model target ─────────────────────────────── */}
            {showDataModelHere && (
                <div className="mb-4 border border-border rounded-sm bg-card p-3" data-testid="wiz-data-model-card">
                    <div className="eyebrow mb-2">Data Model</div>
                    {!creating ? (
                        <div className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                                <label htmlFor="wiz-data-model-select" className="text-[11px] font-medium text-muted-foreground">
                                    Rows from this ingest will land in
                                </label>
                                <select
                                    id="wiz-data-model-select"
                                    data-testid="wiz-data-model-select"
                                    value={dataModelSlug || ""}
                                    onChange={(e) => onModelChange(e.target.value)}
                                    className={`mt-1 w-full text-xs font-medium bg-background border rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40 ${dataModelSlug ? "border-border" : "border-red-500"}`}
                                >
                                    <option value="" disabled>Select a target data model…</option>
                                    {sortedModels.map((m) => (
                                        <option key={m.slug} value={m.slug} disabled={!selectable(m)} data-testid={`wiz-data-model-option-${m.slug}`}>
                                            {m.name || m.slug} — {!selectable(m) && m.valid !== false ? "Not allowed: connector is bound to an enforced model" : m.reason}
                                        </option>
                                    ))}
                                    {!enforcedSlugs.length && <option value="__create_new__">+ Create new data model…</option>}
                                </select>
                                <div className={`text-[11px] mt-1 ${dataModelSlug ? "text-muted-foreground" : "text-red-600"}`} data-testid="wiz-data-model-hint">
                                    {!dataModelSlug
                                        ? (enforcedSlugs.length > 1 ? `This connector is bound to ${enforcedSlugs.length} enforced models — choose one. Nothing is ingested until you do.` : "Choose a target data model. Nothing is ingested until you do.")
                                        : legacyTarget && dataModelSlug === legacyTarget ? "Current target of this existing connection — unchanged unless you pick another model."
                                        : enforcedSlugs.length === 1 && dataModelSlug === enforcedSlugs[0] ? "Pre-selected: the only enforced model that lists this connector." : ""}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-1" data-testid="wiz-data-model-name">
                                    {selectedModel ? (selectedModel.name || selectedModel.slug) : ""}
                                </div>
                            </div>
                            <span className="text-[11px] px-2 py-1 rounded-sm bg-primary/10 text-primary shrink-0" data-testid="wiz-data-model-mode">
                                {isCloned ? "Enrich cloned model" : "Enrich existing"}
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-end gap-2" data-testid="wiz-data-model-create">
                            <div className="flex-1 min-w-0">
                                <label htmlFor="wiz-new-model-name" className="text-[11px] font-medium text-muted-foreground">
                                    Name for the new data model (structure will be cloned from <b>{selectedModel?.name || targetSlug}</b>)
                                </label>
                                <input
                                    id="wiz-new-model-name"
                                    data-testid="wiz-new-model-name"
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder="e.g. Marketing — Acme Client"
                                    autoFocus
                                    className="mt-1 w-full text-xs bg-background border border-border rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                />
                            </div>
                            <Button size="sm" className="rounded-sm" onClick={submitCreate}
                                disabled={busyCreate || !newName.trim()} data-testid="wiz-new-model-submit">
                                {busyCreate && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                                Create
                            </Button>
                            <Button size="sm" variant="outline" className="rounded-sm"
                                onClick={cancelCreate} disabled={busyCreate}
                                data-testid="wiz-new-model-cancel">Cancel</Button>
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="eyebrow">Detected relationships ({relationships.length})</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Auto-inferred from column names + PK/FK hints. Remove anything incorrect or add your own.
                    </div>
                </div>
                <Button size="sm" variant="outline" onClick={addRel} className="rounded-sm" data-testid="wiz-rel-add">
                    <Plus className="h-3.5 w-3.5 mr-1" />Add
                </Button>
            </div>
            {selected.length >= 2 && relationships.length > 0 && (
                <div className="mb-3 bg-primary/5 border border-primary/20 rounded-sm px-3 py-2 text-xs flex items-start gap-2" data-testid="wiz-unified-note">
                    <Sparkles className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    <span>On ingest, we'll also create a <b>unified joined dataset</b> — in addition to one dataset per table — so you can analyze {selected.length} tables together using these joins.</span>
                </div>
            )}
            {selected.length >= 2 && (
                <div className="mb-3 border border-border rounded-sm bg-card px-3 py-2.5" data-testid="wiz-fact-table-card">
                    <label htmlFor="wiz-fact-table-select" className="eyebrow block mb-1">Base table for the combined view</label>
                    <select
                        id="wiz-fact-table-select"
                        data-testid="wiz-fact-table-select"
                        value={factTable || ""}
                        onChange={(e) => setFactTable?.(e.target.value || null)}
                        className="w-full text-xs font-medium bg-background border border-border rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                        <option value="">Auto — largest table (default)</option>
                        {selectedQualifiedTables.map((q) => (
                            <option key={q} value={q}>{q}</option>
                        ))}
                    </select>
                    <p className="text-[11px] text-muted-foreground mt-1">
                        The combined view keeps this table's grain (one row per record); the others join on as dimensions. Pick your fact table (e.g. <b>Orders</b>) to avoid row inflation.
                    </p>
                </div>
            )}
            <div className="border border-border rounded-sm bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-secondary/30">
                        <tr>
                            <th className="text-left px-2 py-2 eyebrow">From</th>
                            <th className="text-left px-2 py-2 eyebrow">→</th>
                            <th className="text-left px-2 py-2 eyebrow">To</th>
                            <th className="text-left px-2 py-2 eyebrow">Type</th>
                            <th className="text-left px-2 py-2 eyebrow">Confidence</th>
                            <th className="text-left px-2 py-2 eyebrow">Note</th>
                            <th className="px-2 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {relationships.map((r, i) => (
                            <tr key={i} className="border-t border-border" data-testid={`wiz-rel-${i}`}>
                                <td className="px-2 py-1.5">
                                    <TableColSelect tables={selectedQualifiedTables} table={r.from_table} col={r.from_column}
                                        colsFor={allColsFor}
                                        onTable={(v) => updateRel(i, { from_table: v, from_column: "" })}
                                        onCol={(v) => updateRel(i, { from_column: v })} testId={`wiz-rel-from-${i}`} />
                                </td>
                                <td className="px-2 py-1.5 text-center"><Link2 className="h-3.5 w-3.5 text-muted-foreground inline" /></td>
                                <td className="px-2 py-1.5">
                                    <TableColSelect tables={selectedQualifiedTables} table={r.to_table} col={r.to_column}
                                        colsFor={allColsFor}
                                        onTable={(v) => updateRel(i, { to_table: v, to_column: "" })}
                                        onCol={(v) => updateRel(i, { to_column: v })} testId={`wiz-rel-to-${i}`} />
                                </td>
                                <td className="px-2 py-1.5">
                                    <select value={r.type} onChange={(e) => updateRel(i, { type: e.target.value })}
                                        className="h-7 px-1 bg-transparent border border-border rounded-sm text-xs font-mono">
                                        <option value="one_to_many">1 → N</option>
                                        <option value="one_to_one">1 → 1</option>
                                        <option value="many_to_many">N ↔ N</option>
                                    </select>
                                </td>
                                <td className="px-2 py-1.5">
                                    <ConfidenceBadge value={r.confidence} />
                                </td>
                                <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[160px]" title={r.note}>{r.note}</td>
                                <td className="px-2 py-1.5 text-right">
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive"
                                        onClick={() => deleteRel(i)} data-testid={`wiz-rel-del-${i}`}>
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {relationships.length === 0 && (
                            <tr><td colSpan={7} className="text-center text-muted-foreground py-4">No relationships detected. Single-table ingest works fine — dashboards will render per-table.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Save & Schedule (inline cron) */}
            <div className="mt-4 border border-border rounded-sm bg-card p-4" data-testid="wiz-schedule">
                <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="eyebrow">Auto-refresh schedule</span>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer mb-2">
                    <input type="checkbox" checked={enableSchedule}
                        onChange={(e) => setEnableSchedule(e.target.checked)}
                        data-testid="wiz-schedule-toggle" />
                    <span>Automatically re-pull these tables on a schedule</span>
                </label>
                {enableSchedule && (
                    <div className="flex items-center gap-2 mt-2 text-xs ml-6" data-testid="wiz-schedule-fields">
                        <span className="text-muted-foreground">Every</span>
                        <input type="number" min="1" max="720" value={scheduleHours}
                            // iter-120 (Feb 2026) — accept transient empty
                            // so backspace works; clamp only on blur.
                            onChange={(e) => {
                                const raw = e.target.value;
                                setScheduleHours(raw === "" ? "" : Number(raw));
                            }}
                            onBlur={(e) => {
                                const n = Number(e.target.value);
                                if (!Number.isFinite(n) || n < 1) {
                                    setScheduleHours(1);
                                }
                            }}
                            className="w-16 h-7 px-2 bg-transparent border border-border rounded-sm font-mono text-xs"
                            data-testid="wiz-schedule-hours" />
                        <span className="text-muted-foreground">hours</span>
                        <span className="ml-3 font-mono text-[10px] text-muted-foreground">
                            cron: <code className="bg-secondary/50 px-1">0 */{Math.max(1, Number(scheduleHours) || 24)} * * *</code>
                        </span>
                    </div>
                )}
                <div className="mt-2 ml-6 text-[10px] text-muted-foreground leading-snug">
                    On ingest, we'll run cursor-based incremental sync using your chosen <b>incremental cursor</b> columns —
                    only new/changed rows are pulled each run.
                </div>
            </div>
        </div>
    );
}


/* ============================================================
   Step: CONFIGURE — connection name + driver-specific fields
   Standard Configure-page pattern for every connector.
   ============================================================ */
export function ConfigureStep({ ctx, FieldInput }) {
    const {
        name, setName, driver, fieldGroups, oauthMerge, oauthOverride,
        setOauthOverride, config, updateCfg, isSample,
        oauthProvider, credentialSets, missingKeys,
    } = ctx;
    const missing = missingKeys instanceof Set ? missingKeys : new Set(missingKeys || []);

    // ─── Universal, driver-agnostic field classification ───
    const allFields = Object.values(fieldGroups || {}).flat();
    const oauthMergeFields = oauthMerge?.fields || [];
    const bucket = { connection: [], credentials: [], advanced: [], sample: [], hidden: [] };
    for (const f of allFields) {
        const cat = classifyField(f, oauthMergeFields);
        if (bucket[cat]) bucket[cat].push(f);
    }
    const hasSampleMode = bucket.sample.length > 0;

    // Multi-cred OAuth — the credential-set selector is the first control
    // of the Credentials section. Hidden when the OAuth-merge fields are
    // filtered out by `show_if` (e.g. HubSpot "Private App Token" mode).
    // In sample mode it stays in place (disabled) so the layout never jumps.
    const activeSets = (credentialSets || []).filter((s) => !s.deactivated);
    const oauthMergeVisible = allFields.some((f) => oauthMergeFields.includes(f.key));
    const showCredSelector = !!oauthProvider
        && (oauthMergeFields.length === 0 || oauthMergeVisible);
    const selectedSetId = (config && config.oauth_credential_set_id) || "";
    const chosenSet = activeSets.find((s) => s.id === selectedSetId) || null;
    // No saved credential set → ask for the OAuth client fields right in the
    // Credentials card; on save the backend stores them as a new set.
    const inlineOauth = showCredSelector && activeSets.length === 0;
    if (inlineOauth) {
        const oauthFields = bucket.advanced.filter((f) => oauthMergeFields.includes(f.key))
            .map((f) => (oauthMerge?.has_credentials ? f : { ...f, required: true }));
        bucket.advanced = bucket.advanced.filter((f) => !oauthMergeFields.includes(f.key));
        bucket.credentials = [...oauthFields, ...bucket.credentials];
    }
    const advancedHasOauthOverrides = bucket.advanced.some((f) => oauthMergeFields.includes(f.key));

    // Credentials status (header, right side): required fields still empty,
    // or "All saved" once every secret has a value.
    const credentialFields = bucket.credentials;
    const isEmpty = (v) => v === null || v === undefined || String(v).trim() === "";
    const secretFields = credentialFields.filter((f) => f.type === "password");
    const credentialsAllSaved = secretFields.length > 0 && secretFields.every((f) => !isEmpty(config[f.key]));
    const requiredEmpty = credentialFields.filter((f) => f.required && f.type !== "boolean" && isEmpty(config[f.key]));
    const overrideCount = Object.values(oauthOverride || {}).filter(Boolean).length;

    // Advanced settings: collapsed by default; opens when overrides are
    // already active, or whenever a validation pass flags a field inside it
    // (`missingKeys` is a fresh Set on every Next, so it re-opens each time).
    const [advancedOpen, setAdvancedOpen] = React.useState(overrideCount > 0);
    const advancedHasMissing = bucket.advanced.some((f) => missing.has(f.key));
    React.useEffect(() => {
        if (advancedHasMissing) setAdvancedOpen(true);
    }, [missingKeys]);

    const renderField = (f) => {
        const sourced = oauthMerge.has_credentials
            && oauthMergeFields.includes(f.key)
            && !oauthOverride[f.key];
        return (
            <FieldInput key={f.key} field={f}
                value={sourced ? oauthMerge.values[f.key] : config[f.key]}
                onChange={(v) => updateCfg(f.key, v)}
                disabled={isSample || sourced}
                invalid={missing.has(f.key)}
                hint={sourced ? "From the selected credential set" : null}
                onOverride={sourced ? () => {
                    setOauthOverride((p) => ({ ...p, [f.key]: true }));
                    if ((config[f.key] || "") === oauthMerge.values[f.key]) updateCfg(f.key, "");
                } : null}
            />
        );
    };

    const nameInvalid = missing.has("__name");
    const nameCell = (
        <div className="min-w-0">
            <label className="field-label mb-1.5 block" htmlFor="wiz-name-input" data-testid="wiz-label-name">
                Connection Name<span className="text-primary"> *</span>
            </label>
            <input id="wiz-name-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={driver.name}
                aria-invalid={nameInvalid || undefined}
                className={`w-full h-9 px-3 bg-transparent border rounded-sm text-sm ${nameInvalid ? "border-destructive focus-visible:outline-destructive" : "border-border"}`}
                data-testid="wiz-name" />
            <div className="text-[11px] text-muted-foreground mt-1 truncate">Shown in your Data sources list.</div>
        </div>
    );

    const credSelectorCell = showCredSelector && !inlineOauth ? (
        <div className="min-w-0" data-testid="wiz-oauth-cred-cell">
            <label className="field-label mb-1.5 block" data-testid="wiz-label-oauth-cred-set">
                OAuth Credential Set{activeSets.length > 0 && <span className="text-primary"> *</span>}
            </label>
            {activeSets.length === 0 ? (
                <>
                    <div className="h-9 px-3 flex items-center border border-dashed border-border rounded-sm text-[12px] text-muted-foreground truncate"
                         data-testid="wiz-oauth-cred-none">
                        No credential set saved for {oauthProvider}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 truncate">
                        {advancedHasOauthOverrides ? (
                            <>Add one in Settings → OAuth credentials, or{" "}
                                <button type="button" onClick={() => setAdvancedOpen(true)}
                                    className="text-primary hover:underline" data-testid="wiz-oauth-enter-manually">
                                    enter Client ID &amp; secret
                                </button>.
                            </>
                        ) : "An admin can add one in Settings → OAuth credentials."}
                    </div>
                </>
            ) : (
                <>
                    <select
                        value={selectedSetId}
                        onChange={(e) => updateCfg("oauth_credential_set_id", e.target.value)}
                        className="w-full h-9 px-3 bg-background border border-border rounded-sm text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="wiz-oauth-cred-select"
                        disabled={activeSets.length === 1 || isSample}
                    >
                        {activeSets.length !== 1 && !chosenSet && (
                            <option value="">— select credential set —</option>
                        )}
                        {activeSets.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.is_default ? "★ " : ""}{s.name}
                            </option>
                        ))}
                    </select>
                    <div className="text-[11px] text-muted-foreground mt-1 truncate"
                         title={chosenSet?.client_id ? `Client ID ${chosenSet.client_id}` : undefined}>
                        {chosenSet || activeSets.length === 1
                            ? "Supplies the Client ID and secret."
                            : "Pick which saved app to sign in with."}
                    </div>
                </>
            )}
        </div>
    ) : null;

    const showCredentials = showCredSelector || credentialFields.length > 0;
    const credentialsStatus = isSample ? null
        : requiredEmpty.length
            ? <span className="text-amber-600 dark:text-amber-500">{requiredEmpty.length} to enter</span>
            : credentialsAllSaved
                ? <span className="inline-flex items-center gap-1 text-[hsl(var(--success))]"><CheckCircle2 className="h-3 w-3" />All saved</span>
                : (showCredSelector && chosenSet && secretFields.length === 0)
                    ? <span className="inline-flex items-center gap-1 text-[hsl(var(--success))]"><CheckCircle2 className="h-3 w-3" />Ready</span>
                    : null;

    const advancedTuningCount = bucket.advanced.length - bucket.advanced.filter((f) => oauthMergeFields.includes(f.key)).length;
    const advancedDescription = advancedHasOauthOverrides && advancedTuningCount > 0
        ? "OAuth overrides and optional tuning — defaults work for most setups."
        : advancedHasOauthOverrides
            ? "Override the OAuth Client ID / secret for this connector only."
            : "Optional tuning — defaults work for most setups.";

    return (
        <div data-testid="wiz-step-configure">
            <div className="space-y-3">
                {/* 1 · Connection details */}
                <ConfigureSection
                    title="Connection details"
                    description={`What to connect in ${driver.name}.`}
                    testId="wiz-card-connection"
                >
                    <FieldGrid fields={bucket.connection} render={renderField} lead={nameCell} />
                </ConfigureSection>

                {/* 2 · Credentials */}
                {showCredentials && (
                    <ConfigureSection
                        title="Credentials"
                        description={inlineOauth
                            ? `How Analyzio signs in to ${driver.name}. Your Client ID & secret are saved as a credential set in Settings → OAuth Credentials.`
                            : `How Analyzio signs in to ${driver.name}.`}
                        right={credentialsStatus}
                        testId="wiz-card-credentials"
                    >
                        <FieldGrid fields={credentialFields} render={renderField} lead={credSelectorCell} />
                    </ConfigureSection>
                )}

                {/* 3 · Advanced settings (collapsed) */}
                {bucket.advanced.length > 0 && (
                    <AdvancedSection
                        open={advancedOpen}
                        onToggle={setAdvancedOpen}
                        badge={overrideCount > 0
                            ? `${overrideCount} override${overrideCount === 1 ? "" : "s"} active`
                            : `${bucket.advanced.length} available`}
                        description={advancedDescription}
                        testId="wiz-card-advanced"
                    >
                        <FieldGrid fields={bucket.advanced} render={renderField} />
                    </AdvancedSection>
                )}
            </div>

            {/* Optional · sample data — single line, after all connection details */}
            {hasSampleMode && (
                <div className="mt-5 pt-4 border-t border-border flex items-center gap-3 min-w-0" data-testid="wiz-sample-line">
                    <Switch
                        id="wiz-sample-switch"
                        checked={!!config.use_sample_data}
                        onCheckedChange={(v) => updateCfg("use_sample_data", !!v)}
                        data-testid="wiz-field-use_sample_data"
                    />
                    <label htmlFor="wiz-sample-switch" className="text-[13px] font-medium cursor-pointer shrink-0">
                        Use sample data
                    </label>
                    <span className="text-[12px] text-muted-foreground truncate" data-testid="wiz-sample-line-text"
                        title={isSample
                            ? "Sample data is on. Live fields above are skipped; you'll explore built-in demo data."
                            : "Preview this connector with demo data, no credentials needed."}>
                        {isSample
                            ? <>— <b className="font-medium text-foreground">on</b> · live fields above are skipped; you&apos;ll explore built-in demo data.</>
                            : "— preview this connector with demo data, no credentials needed."}
                    </span>
                </div>
            )}
        </div>
    );
}


/* ============================================================
   Step: TEST — connection check + OAuth + preview + DB picker
   ============================================================ */
export function TestStep({ ctx, InlineOAuthCredsCard, DatabasePicker }) {
    const {
        oauthProvider, isSample, hasOAuthToken, busy, oauthMerge, driver, config,
        startOAuth, runTest, testResult, updateCfg, setOauthMerge, api,
    } = ctx;

    return (
        <div data-testid="wiz-step-test" className="text-center py-6">
            {/* iter-119af — OAuth Required block ONLY rendered when token
                is missing. Previously rendered alongside a green "Connected"
                badge, which looked self-contradictory. Once connected, the
                badge below the Test button is enough confirmation. */}
            {oauthProvider && !isSample && !hasOAuthToken && (() => {
                const isYamlOAuth = (driver?.is_yaml || !!driver?.auth_type) && ["oauth2_authcode", "oauth2_pkce"].includes(driver?.auth_type || driver?.spec?.auth?.type);
                const hasEnteredCreds = !!(config?.client_id || config?.oauth_client_id || config?.app_id) && !!(config?.client_secret || config?.oauth_client_secret || config?.app_secret);
                const canConnectOAuth = hasEnteredCreds || isYamlOAuth || oauthMerge.has_credentials || hasOAuthToken;
                return (
                    <div className="mb-5 max-w-md mx-auto border border-primary/30 bg-primary/5 rounded-sm p-4 text-left">
                        <div className="flex items-center gap-2 mb-2">
                            <PlugZap className="h-4 w-4 text-primary" />
                            <span className="eyebrow">OAuth required</span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                            Sample mode is off — connect to {driver.name} via OAuth. The provider's consent screen will open in a popup.
                        </p>
                        {!isYamlOAuth && !hasEnteredCreds && !oauthMerge.has_credentials && (
                            <InlineOAuthCredsCard
                                provider={oauthProvider}
                                hasCredentials={oauthMerge.has_credentials}
                                onSaved={async () => {
                                    try {
                                        const r = await api.get(`/data/connectors-aux/oauth-merged-fields?key=${driver.key}`);
                                        setOauthMerge(r.data || oauthMerge);
                                    } catch { /* no-op */ }
                                }}
                            />
                        )}
                        <Button onClick={startOAuth} disabled={busy || !canConnectOAuth} className="rounded-sm" data-testid="wiz-oauth-btn">
                            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Working…</> : <><PlugZap className="h-4 w-4 mr-2" />Connect with OAuth</>}
                        </Button>
                    </div>
                );
            })()}
            {oauthProvider && !isSample && hasOAuthToken && (
                <div className="mb-5 max-w-md mx-auto flex items-center justify-between gap-3 px-3.5 py-2.5 border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5 rounded-sm text-left">
                    <div className="flex items-center gap-2.5">
                        <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))] shrink-0" />
                        <div className="text-xs">
                            <span className="font-medium text-foreground">OAuth connected for your organization</span>
                            <span className="block text-[11px] text-muted-foreground">Credentials active on file. Click Test connection to proceed.</span>
                        </div>
                    </div>
                    <button onClick={startOAuth} disabled={busy} className="text-[11px] font-mono text-primary hover:underline underline-offset-2 shrink-0" data-testid="wiz-oauth-reauth">
                        Re-authorize
                    </button>
                </div>
            )}
            {isSample && !oauthProvider && (
                <div className="mb-5 max-w-md mx-auto border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 text-left flex items-start gap-2" data-testid="wiz-sample-warning">
                    <span className="text-amber-500 text-sm leading-none mt-0.5">⚠</span>
                    <div className="text-xs leading-relaxed">
                        <div className="font-medium mb-0.5">Sample mode is ON</div>
                        <div className="text-muted-foreground">"Test connection" will return a canned "Sample mode ready" response — it is <b>not</b> reaching your real server. Toggle <code className="font-mono">Use sample data</code> off under Connection to test live credentials.</div>
                        <button type="button" onClick={() => updateCfg("use_sample_data", false)} className="mt-2 text-[11px] font-mono text-primary hover:underline" data-testid="wiz-sample-disable">
                            → Disable sample mode &amp; test the real server
                        </button>
                    </div>
                </div>
            )}
            <Button onClick={runTest} disabled={busy || (oauthProvider && !isSample && !hasOAuthToken)} className="rounded-sm" size="lg" data-testid="wiz-test-btn">
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Testing…</> : <><Zap className="h-4 w-4 mr-2" />Test connection</>}
            </Button>
            {testResult && (() => {
                // iter-119af — when the test failed with the well-known
                // "ad_account_id required" message, the failure isn't
                // really a failure — it just means the user hasn't reached
                // the Accounts step yet. Show a friendly "Next: pick an
                // account" prompt instead of a scary red error.
                const msg = String(testResult.message || "");
                const needsAccountPick = !testResult.ok && /ad_account_id required|account_id required|customer_id required|property_id required/i.test(msg);
                if (needsAccountPick) {
                    return (
                        <div className="mt-5 max-w-2xl mx-auto text-left" data-testid="wiz-test-result">
                            <div className="flex items-baseline gap-2 text-sm">
                                <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))] shrink-0 translate-y-0.5" />
                                <span className="font-medium">Credentials look good</span>
                                <span className="text-muted-foreground text-xs flex-1 min-w-0">Token is valid — continue to pick which account(s) to sync.</span>
                                {testResult.latency_ms != null && (
                                    <span className="text-[10px] font-mono text-muted-foreground shrink-0">{testResult.latency_ms}ms</span>
                                )}
                            </div>
                        </div>
                    );
                }
                return (
                <div className="mt-5 max-w-2xl mx-auto text-left" data-testid="wiz-test-result">
                    {/* iter-119av — inline status (no box). Header is a single
                        line: icon · status word · short message · latency.
                        Account preview is a comma-separated meta line.
                        Table counts are inline pills. Nothing is wrapped in
                        a border/bg-tinted card. */}
                    <div className={`flex items-baseline gap-2 text-sm ${
                        testResult.ok ? "text-foreground" : "text-destructive"
                    }`}>
                        {testResult.ok
                            ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))] shrink-0 translate-y-0.5" />
                            : <XCircle className="h-4 w-4 text-destructive shrink-0 translate-y-0.5" />}
                        <span className="font-medium">{testResult.ok ? "Connected" : "Failed"}</span>
                        <span className="text-muted-foreground font-mono text-xs break-words flex-1 min-w-0">{testResult.message}</span>
                        {testResult.latency_ms != null && (
                            <span className="text-[10px] font-mono text-muted-foreground shrink-0">{testResult.latency_ms}ms</span>
                        )}
                    </div>
                    {testResult.server_version && (
                        <div className="ml-6 mt-0.5 text-[10px] font-mono text-muted-foreground/70">{testResult.server_version}</div>
                    )}
                    {testResult.ok && testResult.preview && Object.keys(testResult.preview).length > 0 && !testResult.preview.error && (
                        <div className="ml-6 mt-3 space-y-1.5" data-testid="wiz-preview-card">
                            {testResult.preview.account && (
                                <div className="text-xs font-mono text-muted-foreground">
                                    <span className="text-foreground">{testResult.preview.account}</span>
                                    {testResult.preview.currency ? <span> · {testResult.preview.currency}</span> : null}
                                    {testResult.preview.status ? <span> · {testResult.preview.status}</span> : null}
                                    {testResult.preview.lookback_days ? <span> · last {testResult.preview.lookback_days}d</span> : null}
                                    {testResult.preview.mode === "live" ? (
                                        <span className="ml-2 text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]">LIVE</span>
                                    ) : testResult.preview.mode === "sample" ? (
                                        <span className="ml-2 text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-700 dark:text-amber-300">SAMPLE</span>
                                    ) : null}
                                </div>
                            )}
                            {testResult.preview.tables && Object.keys(testResult.preview.tables).length > 0 && (
                                <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px]">
                                    {Object.entries(testResult.preview.tables).map(([t, n]) => {
                                        const empty = n === 0;
                                        const unknown = n === null;
                                        return (
                                            <span key={t} className="inline-flex items-baseline gap-1">
                                                <span className="text-muted-foreground">{t}</span>
                                                <span className={empty ? "text-amber-600 dark:text-amber-400" : unknown ? "text-muted-foreground" : "text-foreground font-medium"}>
                                                    {unknown ? "—" : Number(n).toLocaleString()}
                                                </span>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            {/* iter-120 round-4 follow-up #4 — removed the
                                "reachable but empty lookback" amber strip
                                per user direction. The empty-tables case
                                is implicit from the row counts already
                                shown above. */}
                        </div>
                    )}
                    {testResult.ok && testResult.preview?.error && (
                        <div className="ml-6 mt-1.5 text-[10px] text-muted-foreground">
                            Preview unavailable: {testResult.preview.error}
                        </div>
                    )}
                    {!testResult.ok && testResult.code === "reauthorize_required" && oauthProvider && (
                        <div className="ml-6 mt-2" data-testid="wiz-test-reauth-cta">
                            <button type="button" onClick={startOAuth} disabled={busy}
                                    className="text-[11px] font-mono text-primary hover:underline"
                                    data-testid="wiz-test-reauth-btn">
                                → Re-authorize {driver.name} now
                            </button>
                        </div>                    )}
                </div>
                );
            })()}
            {testResult && testResult.ok && (
                <DatabasePicker
                    driver={driver}
                    cfg={config}
                    onPick={(name) => updateCfg("database", name)}
                />
            )}
            {!testResult && (
                <p className="mt-4 text-xs text-muted-foreground max-w-sm mx-auto">
                    Verifies that the credentials above can reach the server. In sample mode this is a no-op.
                </p>
            )}
        </div>
    );
}


/* ============================================================
   Step: ACCOUNTS — pick one or more accounts to scope ingest
   ============================================================ */
export function AccountsStep({ ctx }) {
    const {
        accountsLoading, accountsError, accounts, selectedAccountIds, setSelectedAccountIds,
    } = ctx;

    return (
        <div data-testid="wiz-step-accounts" className="space-y-4">
            <div>
                <div className="eyebrow">Select account(s) to sync</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                    Choose one or more accounts from the credentials above. The connector will scope schemas, tables and ingest to the selected account(s) only.
                </div>
            </div>
            {accountsLoading && (
                <div className="flex items-center gap-2 text-[12px] text-muted-foreground py-4" data-testid="accounts-loading">
                    <Loader2 className="h-4 w-4 animate-spin" /> Discovering accounts…
                </div>
            )}
            {!accountsLoading && accountsError && (
                <div className="border border-destructive/40 bg-destructive/5 rounded-sm p-3 text-[12px]" data-testid="accounts-error">
                    <div className="font-medium text-destructive mb-1 flex items-center gap-1.5"><XCircle className="h-3.5 w-3.5" />Could not list accounts</div>
                    <div className="text-muted-foreground">{accountsError}</div>
                    <div className="text-muted-foreground mt-2">You can still proceed using the account ID you typed in step 1 — just press Next.</div>
                </div>
            )}
            {!accountsLoading && !accountsError && accounts.length === 0 && (
                <div className="text-[12px] text-muted-foreground py-4" data-testid="accounts-empty">
                    No discoverable accounts on these credentials. Continuing will use the account ID you typed in step 1.
                </div>
            )}
            {!accountsLoading && accounts.length > 0 && (
                <div className="space-y-1.5" data-testid="accounts-list">
                    <div className="grid grid-cols-12 gap-2 px-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <div className="col-span-1"></div>
                        <div className="col-span-3">Account ID</div>
                        <div className="col-span-5">Name</div>
                        <div className="col-span-2">Currency</div>
                        <div className="col-span-1">Status</div>
                    </div>
                    {accounts.map((a) => {
                        const id = a.id || a.account_id || a.customer_id || a.property_id;
                        const checked = selectedAccountIds.includes(id);
                        return (
                            <label
                                key={id}
                                className={`grid grid-cols-12 gap-2 items-center px-2 py-2 border rounded-sm cursor-pointer ${checked ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"}`}
                                data-testid={`account-row-${id}`}
                            >
                                <div className="col-span-1">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                            setSelectedAccountIds((prev) =>
                                                prev.includes(id)
                                                    ? prev.filter((x) => x !== id)
                                                    : [...prev, id]
                                            );
                                        }}
                                        data-testid={`account-checkbox-${id}`}
                                    />
                                </div>
                                <div className="col-span-3 font-mono text-[11px]">{id}</div>
                                <div className="col-span-5 text-[12px] truncate" title={a.name}>{a.name || "—"}</div>
                                <div className="col-span-2 text-[11px]">{a.currency || "—"}</div>
                                <div className="col-span-1 text-[10px] uppercase tracking-wider">{a.status || a.account_status || "—"}</div>
                            </label>
                        );
                    })}
                    <div className="text-[10px] text-muted-foreground mt-2">
                        Selected {selectedAccountIds.length} of {accounts.length} · The first selected becomes the primary account / property for this connector.
                    </div>
                </div>
            )}
        </div>
    );
}


/* ============================================================
   Step: MAPPING — mandatory column-map confirmation step
   (iter-119bd Phase 4)
   ============================================================
   For non-prebuilt connectors (Postgres / MySQL / SQL Server /
   generic SQLAlchemy) the connector's source schema is user-defined,
   so we MUST surface the auto-detected canonical-model match for
   review BEFORE downstream dashboards rely on the model_rows
   writeback. Prebuilt SaaS connectors (Meta Ads, GA4, Google Ads)
   skip this step entirely — their schemas target the canonical
   "marketing" model with a deterministic field map.

   Renders one SchemaMatchPreview per dataset created by the ingest.
   The user can confirm each mapping in place (it auto-PATCHes the
   field map + re-ingests on save) and then click Done. */
export function MappingStep({ ctx }) {
    const {
        ingestedDatasets,
        driver,
        dataModels: parentDataModels,
        dataModelSlug: parentSlug,
        setDataModelSlug: parentSetSlug,
        setDataModels: parentSetDataModels,
        selected,
    } = ctx;
    const datasets = ingestedDatasets || [];

    // ── iter-121.165 — Pre-ingest Data Model picker for SQL flows ──
    // Marketing drivers declare `target_model_slug` and rely on the
    // ConnectorWizard's useEffect to seed dataModels + auto-select
    // (Phase 2). SQL drivers don't declare one — this MappingStep
    // fetches ALL models on mount and auto-selects the best keyword
    // match from the selected table names. Universal — no per-driver
    // hardcoding, works for SQL Server / MySQL / Postgres / future
    // DB drivers alike.
    const [sqlDataModels, setSqlDataModels] = React.useState(parentDataModels || []);
    const [creating, setCreating] = React.useState(false);
    const [newName, setNewName] = React.useState("");
    const [newCategory, setNewCategory] = React.useState("Marketing");
    const [busyCreate, setBusyCreate] = React.useState(false);

    const targetSlug = driver?.target_model_slug || null;
    const isPreIngest = datasets.length === 0;

    // Detect industry from selected table names + column hints. Pure
    // keyword heuristic — safe fallback is null (no auto-select).
    const detectIndustry = React.useCallback((selectedTables) => {
        const tokens = new Set(
            (selectedTables || []).flatMap((t) => (t.name || "").toLowerCase().split(/[_\s-]+/))
        );
        const buckets = {
            marketing:  ["marketing", "campaign", "campaigns", "ads", "impressions", "clicks", "spend", "keywords", "audience", "channel"],
            ecommerce:  ["ecommerce", "order", "orders", "product", "products", "cart", "checkout", "variant", "sku", "customers", "customer"],
            finance:    ["finance", "invoice", "invoices", "payment", "payments", "revenue", "expense", "expenses", "ledger", "transactions"],
            hr:         ["hr", "employee", "employees", "attrition", "salary", "department", "payroll", "hiring"],
            sales:      ["sales", "deal", "deals", "opportunity", "opportunities", "leads", "pipeline", "quote"],
        };
        let best = null; let bestScore = 0;
        for (const [slug, kws] of Object.entries(buckets)) {
            const score = kws.reduce((n, kw) => n + (tokens.has(kw) ? 1 : 0), 0);
            if (score > bestScore) { best = slug; bestScore = score; }
        }
        return bestScore > 0 ? best : null;
    }, []);

    // Load all data models + auto-pick when this step opens without a
    // driver-level target (SQL case). Marketing drivers already have
    // sqlDataModels seeded from parent state and skip this path.
    React.useEffect(() => {
        if (targetSlug) return;                 // marketing path — parent handled it
        if (!driver?.key) return;
        let cancelled = false;
        (async () => {
            try {
                const api = (await import("@/lib/api")).default;
                const r = await api.get(`/data-models`);
                if (cancelled) return;
                const list = Array.isArray(r.data) ? r.data : [];
                setSqlDataModels(list);
                parentSetDataModels?.(list);
                // Only auto-select when the user hasn't already picked.
                if (!parentSlug) {
                    const guess = detectIndustry(selected);
                    const match = list.find((m) => m.slug === guess);
                    if (match) parentSetSlug?.(match.slug);
                }
            } catch {
                setSqlDataModels([]);
            }
        })();
        return () => { cancelled = true; };
    }, [driver?.key, targetSlug]);

    const models = (parentDataModels && parentDataModels.length) ? parentDataModels : sqlDataModels;
    const selectedModel = models.find((m) => m.slug === parentSlug);

    const onModelChange = (v) => {
        if (v === "__create_new__") { setCreating(true); return; }
        parentSetSlug?.(v);
    };

    const submitCreate = async () => {
        const name = (newName || "").trim();
        const cat  = (newCategory || "").trim();
        if (!name || !cat) return;
        setBusyCreate(true);
        try {
            const api = (await import("@/lib/api")).default;
            // For SQL flows there's no "source model to clone from" —
            // create a bare model. When the SA is IN a marketing flow
            // (target_model_slug present), fall back to /clone so the
            // structure is preserved (Phase 2 semantics).
            const created = targetSlug
                ? (await api.post(`/data-models/clone`, { from_slug: targetSlug, name })).data
                : (await api.post(`/data-models`, {
                    name,
                    slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, ""),
                    category: cat,
                    // Minimum viable schema — the ingest orchestrator's
                    // writeback merges whatever columns the SQL rows
                    // carry on top of this bootstrap field.
                    fields: [{ name: "external_id", type: "string", is_pk: true, description: "Row identity" }],
                  })).data;
            if (created?.slug) {
                const next = [...models, created];
                setSqlDataModels(next);
                parentSetDataModels?.(next);
                parentSetSlug?.(created.slug);
                setCreating(false);
                setNewName("");
            }
        } catch (e) {
            const { toast } = await import("sonner");
            toast.error(e?.response?.data?.detail || e?.message || "Failed to create model");
        } finally {
            setBusyCreate(false);
        }
    };

    // ── PRE-INGEST RENDER (SQL Confirm mapping step) ──
    if (isPreIngest) {
        const guess = detectIndustry(selected);
        return (
            <div data-testid="wiz-step-mapping" className="space-y-4">
                <div className="flex items-start gap-3 border border-primary/30 bg-primary/5 rounded-sm px-4 py-3">
                    <Wand2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="eyebrow">Confirm mapping</div>
                        <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                            Pick the <b>unified data model</b> that will receive rows from every selected table.
                            {guess && (
                                <> Based on the table names, we detected {["a","e","i","o","u"].includes(guess[0]) ? "an" : "a"} <b>{guess}</b> workload and pre-selected the matching model.</>
                            )}
                            {" "}All {selected?.length || 0} selected tables land in this <b>single</b> model — one ingest run enriches one model.
                        </p>
                    </div>
                </div>

                <div className="border border-border rounded-sm bg-card p-3" data-testid="wiz-data-model-card">
                    <div className="eyebrow mb-2">Data Model</div>
                    {!creating ? (
                        <>
                            <label htmlFor="wiz-data-model-select" className="text-[11px] font-medium text-muted-foreground">
                                Rows from all selected tables will land in
                            </label>
                            <select
                                id="wiz-data-model-select"
                                data-testid="wiz-data-model-select"
                                value={parentSlug || ""}
                                onChange={(e) => onModelChange(e.target.value)}
                                className="mt-1 w-full text-xs font-medium bg-background border border-border rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            >
                                <option value="" disabled>{models.length ? "Select a data model…" : "No models available"}</option>
                                {models.map((m) => (
                                    <option key={m.slug} value={m.slug}>
                                        {m.is_system_model ? "★ " : ""}{m.name || m.slug}
                                        {m.category ? ` — ${m.category}` : ""}
                                    </option>
                                ))}
                                <option value="__create_new__">+ Create new data model…</option>
                            </select>
                            <div className="text-[11px] text-muted-foreground mt-1" data-testid="wiz-data-model-name">
                                {selectedModel ? (selectedModel.name || selectedModel.slug) : ""}
                            </div>
                        </>
                    ) : (
                        <div className="space-y-2" data-testid="wiz-data-model-create">
                            <div>
                                <label htmlFor="wiz-new-model-name" className="text-[11px] font-medium text-muted-foreground">Model name</label>
                                <input
                                    id="wiz-new-model-name"
                                    data-testid="wiz-new-model-name"
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder="e.g. Ecommerce — Acme Client"
                                    autoFocus
                                    className="mt-1 w-full text-xs bg-background border border-border rounded-sm px-2 py-1.5"
                                />
                            </div>
                            <div>
                                <label htmlFor="wiz-new-model-category" className="text-[11px] font-medium text-muted-foreground">Industry / category</label>
                                <select
                                    id="wiz-new-model-category"
                                    data-testid="wiz-new-model-category"
                                    value={newCategory}
                                    onChange={(e) => setNewCategory(e.target.value)}
                                    className="mt-1 w-full text-xs bg-background border border-border rounded-sm px-2 py-1.5"
                                >
                                    <option>Marketing</option>
                                    <option>Ecommerce</option>
                                    <option>Finance</option>
                                    <option>HR</option>
                                    <option>Sales</option>
                                    <option>Custom</option>
                                </select>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setCreating(false)} disabled={busyCreate} data-testid="wiz-new-model-cancel">Cancel</Button>
                                <Button size="sm" className="rounded-sm" onClick={submitCreate} disabled={busyCreate || !newName.trim()} data-testid="wiz-new-model-submit">
                                    {busyCreate && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Create
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="text-[11px] text-muted-foreground px-1">
                    Click <b>Ingest</b> below to import all selected tables into the chosen model.
                </div>
            </div>
        );
    }

    // ── POST-INGEST RENDER (existing column-mapping review, unchanged) ──
    return (
        <div data-testid="wiz-step-mapping" className="space-y-4">
            <div className="flex items-start gap-3 border border-primary/30 bg-primary/5 rounded-sm px-4 py-3">
                <Wand2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="eyebrow">Confirm column mapping</div>
                    <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                        Your data is ingested into <b>{datasets.length}</b> dataset{datasets.length !== 1 ? "s" : ""}.
                        Review how each table's columns map onto the platform's canonical
                        data models. Auto-detected mappings (≥70% confidence) are pre-filled —
                        click <b>Remap</b> on any dataset to override fields if needed.
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1.5 italic">
                        Datasets below threshold won't drive dashboards until you manually map their fields.
                    </p>
                </div>
            </div>

            <div className="space-y-3" data-testid="wiz-mapping-datasets">
                {datasets.map((ds) => {
                    const writeback = ds.model_writeback || null;
                    const conf = writeback?.match_confidence;
                    const lowConf = conf != null && conf < 0.7;
                    return (
                        <div key={ds.id} className="space-y-1.5" data-testid={`wiz-mapping-dataset-${ds.id}`}>
                            <div className="flex items-center gap-2">
                                <Database className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="font-mono text-sm font-semibold">{ds.name}</span>
                                <Badge variant="outline" className="font-mono text-[9px]">{(ds.rows || 0).toLocaleString()} rows</Badge>
                                {writeback?.model_slug ? (
                                    <Badge variant="outline" className="font-mono text-[9px] border-primary/40 text-primary">
                                        → model.{writeback.model_slug}
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" className="font-mono text-[9px] border-amber-500/40 text-amber-600">
                                        <AlertCircle className="h-2.5 w-2.5 mr-1 inline" /> unmapped
                                    </Badge>
                                )}
                                {lowConf && (
                                    <Badge variant="outline" className="font-mono text-[9px] border-amber-500/40 text-amber-600">
                                        low confidence
                                    </Badge>
                                )}
                            </div>
                            <SchemaMatchPreview datasetId={ds.id} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}



export function WizardFooter({ ctx }) {
    const { onClose, onBack, stepIdx, setStepIdx, onNext, busy, stepsLen, enableSchedule, currentStepKey, nextStepKey, ingestDone } = ctx;
    // iter-119bd Phase 4 + iter-121.165 — Mapping-aware CTA labels:
    //   • On the mapping step, PRE-ingest (SQL flow) → "Ingest" (fires
    //     ingest with the picked data_model_slug baked in).
    //   • On the mapping step, POST-ingest → "Done" (close wizard).
    //   • Step right before mapping is a pure review step now — say "Next".
    //   • Otherwise → previous "Ingest" / "Next" labels.
    const isLast = stepIdx === stepsLen - 1;
    let label;
    if (isLast) {
        if (currentStepKey === "mapping") {
            label = ingestDone ? "Done" : (enableSchedule ? "Ingest & Schedule" : "Ingest");
        } else if (currentStepKey === "relationships") {
            label = ingestDone ? "Done" : (enableSchedule ? "Ingest & Schedule" : "Done");
        } else if (currentStepKey === "tables") {
            // iter-121.153 — non-SA DB flow: no mapping step follows, ingest
            // fires here and auto-saves the model. Button reads "Ingest Data".
            label = enableSchedule ? "Ingest Data & Schedule" : "Ingest Data";
        } else {
            label = enableSchedule ? "Save · Ingest & Schedule" : "Ingest";
        }
    } else {
        label = "Next";
    }
    return (
        <div className="px-6 py-3 border-t border-border flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={onClose} className="rounded-sm" data-testid="wiz-cancel">Cancel</Button>
            <div className="flex items-center gap-2">
                {stepIdx > 0 && (
                    <Button variant="outline" size="sm" className="rounded-sm"
                        onClick={() => setStepIdx(stepIdx - 1)} disabled={busy}
                        data-testid="wiz-back">
                        <ChevronLeft className="h-3.5 w-3.5 mr-1" />Back
                    </Button>
                )}
                {/* iter-121.162 — Back-to-catalog on step 0. Only rendered
                    when the parent supplies `onBack` (the Add Source flow
                    passes it; the Manage-existing-connector flow does
                    not, because there's no catalog to return to). */}
                {stepIdx === 0 && typeof onBack === "function" && (
                    <Button variant="outline" size="sm" className="rounded-sm"
                        onClick={onBack} disabled={busy}
                        data-testid="wiz-back-to-catalog">
                        <ChevronLeft className="h-3.5 w-3.5 mr-1" />Back
                    </Button>
                )}
                <Button size="sm" className="rounded-sm" onClick={onNext} disabled={busy}
                    data-testid="wiz-next">
                    {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                    {label}
                    {!isLast && !busy && <ChevronRight className="h-3.5 w-3.5 ml-1" />}
                </Button>
            </div>
        </div>
    );
}
